// Customer login + optional self-signup (portal-scoped). Sign-up only shows
// when the portal has `allow_signup` enabled by the admin. Customers without
// an account whose admin sent a magic link use the set-password flow instead.
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { usePortal } from './PortalLayout';
import { Eye, EyeSlash } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function PortalLoginPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { reloadCustomer, portal, customer } = usePortal();
  const allowSignup = portal.allow_signup === true;
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Already authenticated? Don't show the login form — go to their requests.
  useEffect(() => {
    if (customer) navigate(`/help/${slug}/requests`, { replace: true });
  }, [customer, slug, navigate]);

  const theme = portal.branding?.theme_color;

  const submit = async (e) => {
    e?.preventDefault();
    if (mode === 'forgot') {
      if (!email.trim()) return;
      setSubmitting(true);
      try {
        await axios.post(`${API}/customer/forgot-password`,
          { email: email.trim(), portal_slug: slug }, { withCredentials: true });
        toast.success('If an account exists, a reset link has been sent to your email.');
        setMode('login');
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not send reset link');
      } finally { setSubmitting(false); }
      return;
    }
    if (!email.trim() || !password) return;
    if (mode === 'signup' && password.length < 8) return toast.error('Password must be at least 8 characters');
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        await axios.post(`${API}/customer/signup`,
          { email: email.trim(), password, name: name.trim(), portal_slug: slug }, { withCredentials: true });
        toast.success('Account created');
      } else {
        await axios.post(`${API}/customer/login`,
          { email: email.trim(), password, portal_slug: slug }, { withCredentials: true });
        toast.success('Welcome back');
      }
      await reloadCustomer();
      navigate(`/help/${slug}/requests`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (mode === 'signup' ? 'Sign-up failed' : 'Login failed'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="portal-login-page">
      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-lg p-6">
        <h1 className="text-xl font-bold text-zinc-900 font-['Outfit'] mb-4">
          {mode === 'signup' ? `Create your ${portal.name} account` : mode === 'forgot' ? 'Reset your password' : `Log in to ${portal.name}`}
        </h1>
        {mode === 'forgot' && (
          <p className="text-xs text-zinc-500 mb-3 -mt-2">Enter your account email and we'll send you a link to reset your password.</p>
        )}
        <div className="space-y-3">
          {mode === 'signup' && (
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                data-testid="portal-signup-name" />
            </div>
          )}
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
              data-testid="portal-login-email" />
          </div>
          {mode !== 'forgot' && (
          <div>
            <Label className="text-xs">Password</Label>
            <div className="relative">
              <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
                placeholder={mode === 'signup' ? 'At least 8 characters' : ''} data-testid="portal-login-password" />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-400 hover:text-zinc-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="portal-login-password-toggle">
                {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {mode === 'login' && (
              <button type="button" onClick={() => setMode('forgot')}
                className="text-[12px] mt-1.5 font-medium underline" style={{ color: theme }}
                data-testid="portal-forgot-password-link">
                Forgot password?
              </button>
            )}
          </div>
          )}
          <Button type="submit" className="w-full" disabled={submitting}
            style={{ background: theme, color: 'white' }}
            data-testid="portal-login-submit">
            {submitting ? 'Please wait…' : (mode === 'signup' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : 'Sign in')}
          </Button>
        </div>

        {mode === 'forgot' ? (
          <p className="text-[12px] text-zinc-600 mt-4 text-center">
            <button type="button" onClick={() => setMode('login')}
              className="font-semibold underline" style={{ color: theme }}
              data-testid="portal-back-to-login">
              Back to log in
            </button>
          </p>
        ) : allowSignup ? (
          <p className="text-[12px] text-zinc-600 mt-4 text-center" data-testid="portal-auth-toggle">
            {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button type="button" onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}
              className="font-semibold underline" style={{ color: theme }}>
              {mode === 'signup' ? 'Log in' : 'Sign up'}
            </button>
          </p>
        ) : (
          <p className="text-[11px] text-zinc-500 mt-4 text-center">
            Don't have an account? Ask an admin to invite you — they'll send a magic link.
          </p>
        )}
        <p className="text-[11px] text-zinc-500 mt-2 text-center">
          <Link to={`/help/${slug}`} className="text-blue-600 underline">Back to portal</Link>
        </p>
      </form>
    </div>
  );
}
